import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import RestaurantCalendarOpenClosedDays from "../components/restaurant_schedule/RestaurantCalendarOpenClosedDays";
import EstimatedTimePage from "../components/EstimatedTimeCustomersSpend";
import DropDownButton from "../components/DropDownButton";
import RestaurantNotifications from "../components/notifications/RestaurantNotifications";
import InputDayOrHour from "../components/InputDayOrHour"; // keep for other sections
import {
  fetchRestaurantOpenDays,
  updateRestaurant,
  fetchHoursEach,
  fetchPlaceOfTable,
} from "../actions/restaurantActions";
import {
  Container,
  CardHeader,
  CardHeaderTitle,
  ModernButton,
  GridContainer,
  SettingsCard,
  SectionTitle,
  Text,
  TabsContainer,
} from "../components/DefaultImports";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faUtensils,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";

function Notifications() {
  const dispatch = useDispatch();
  const params = useParams();
  const [restaurant_id, setRestaurant_id] = useState(false);

  const [eachTime, setEachTime] = useState("00:15");
  const [acceptPeoplePerEachTime, setAcceptPeoplePerEachTime] = useState("");
  const [limitPeopleReservation, setLimitPeopleReservation] = useState("");
  const [errorMessage, setErrorMessage] = useState("ok_status");
  const [showError, setShowError] = useState(false);
  const [activeTab, setActiveTab] = useState("restaurant_notifications");

  // ✅ reconfirmations now plain numeric strings like "5"
  const [reconfirmation_duration, setReconfirmation_duration] = useState("");
  const [reconfirmation_time_before_date, setReconfirmation_time_before_date] =
    useState("");

  // other fields stay as they were
  const [messageReminderTime, setMessageReminderTime] = useState("");
  const [timeToCancelledWarning, setTimeToCancelledWarning] = useState("");

  // validation + UI
  const [reconfirmationCompareError, setReconfirmationCompareError] =
    useState("");
  const [didTrySave, setDidTrySave] = useState(false);
  const [touchedBeforeDate, setTouchedBeforeDate] = useState(false);

  const restaurantDetails = useSelector((state) => state.restaurantDetails);
  const { restaurants } = restaurantDetails;

  useEffect(() => {
    if (restaurants) {
      setRestaurant_id(restaurants.id);
      dispatch(fetchPlaceOfTable(restaurants.id));
    }
  }, [restaurants, dispatch]);

  useEffect(() => {
    dispatch(fetchRestaurantOpenDays(restaurant_id));
    dispatch(fetchHoursEach(restaurant_id));
  }, [dispatch, restaurant_id]);

  useEffect(() => {
    if (restaurants) {
      if (restaurants.each_time) setEachTime(restaurants.each_time);
      if (restaurants.accept_people_per_each_time !== undefined)
        setAcceptPeoplePerEachTime(restaurants.accept_people_per_each_time);
      if (restaurants.limit_people_reservation !== undefined)
        setLimitPeopleReservation(restaurants.limit_people_reservation);

      // ✅ ensure reconfirmations are strings
      setReconfirmation_duration(
        restaurants?.reconfirmation_duration != null
          ? String(restaurants.reconfirmation_duration)
          : "",
      );

      setReconfirmation_time_before_date(
        restaurants?.reconfirmation_time_before_date != null
          ? String(restaurants.reconfirmation_time_before_date)
          : "",
      );

      // keep as before
      setMessageReminderTime(restaurants.send_message_reminder_to_customers);
      setTimeToCancelledWarning(restaurants.time_to_cancelled_warning);
    }
  }, [restaurants]);

  const toIntOrNull = (v) => {
    const s = String(v ?? "").trim();
    if (s === "") return null;
    const n = parseInt(s, 10);
    return Number.isFinite(n) ? n : null;
  };

  // ✅ second cannot be greater than first
  useEffect(() => {
    const durationNum = toIntOrNull(reconfirmation_duration);
    const beforeNum = toIntOrNull(reconfirmation_time_before_date);

    if (durationNum != null && beforeNum != null && beforeNum > durationNum) {
      setReconfirmationCompareError(
        "O 2º valor não pode ser maior do que o 1º (antes da reserva ≤ antecedência).",
      );
    } else {
      setReconfirmationCompareError("");
    }
  }, [reconfirmation_duration, reconfirmation_time_before_date]);

  // ✅ handlers now receive strings like "5"
  const handleReconfirmation = (valueStr) => {
    setReconfirmation_duration(valueStr);
  };

  const handleReconfirmation_time_before_date = (valueStr) => {
    setTouchedBeforeDate(true);
    setReconfirmation_time_before_date(valueStr);
  };

  const handleMessageReminderTime = (value) => {
    setMessageReminderTime(value);
  };

  const handleTimeCancelWarning = (value) => {
    setTimeToCancelledWarning(value);
  };

  function checkEmptyStates() {
    let emptyStates = [];
    if (!eachTime) emptyStates.push("Cada tempo");
    if (!acceptPeoplePerEachTime || acceptPeoplePerEachTime <= 0)
      emptyStates.push("Capacidade por horário");
    if (!limitPeopleReservation || limitPeopleReservation <= 0)
      emptyStates.push("Limite por grupo");
    return emptyStates.length > 0
      ? `Preencher: ${emptyStates.join(", ")}`
      : "ok_status";
  }

  function checkForErrorsAndUpdateRestaurant() {
    const status_error = checkEmptyStates();
    setErrorMessage(status_error);

    if (status_error === "ok_status") {
      setShowError(false);

      dispatch(
        updateRestaurant(restaurant_id, {
          time_to_cancelled_warning: timeToCancelledWarning,
          send_message_reminder_to_customers: messageReminderTime,

          // ✅ send as strings like "5"
          reconfirmation_time_before_date: reconfirmation_time_before_date,
          reconfirmation_duration: reconfirmation_duration,
        }),
      );

      toast.success("Guardado com sucesso", {
        style: { color: "#EE754C" },
        icon: (
          <FontAwesomeIcon icon={faCheckCircle} style={{ color: "#EE754C" }} />
        ),
        progressStyle: { backgroundColor: "#EE754C" },
        autoClose: 1000,
      });
    } else {
      setShowError(true);
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    setDidTrySave(true);

    if (reconfirmationCompareError) {
      toast.error(reconfirmationCompareError, { autoClose: 1500 });
      return;
    }

    checkForErrorsAndUpdateRestaurant();
  };

  return (
    <Container>
      <ToastContainer />
      <CardHeader>
        <CardHeaderTitle>Notificações</CardHeaderTitle>
      </CardHeader>

      <TabsContainer>
        <StyledTabButton
          active={activeTab === "restaurant_notifications"}
          onClick={() => setActiveTab("restaurant_notifications")}
        >
          <FontAwesomeIcon icon={faUtensils} /> Restaurante
        </StyledTabButton>

        <StyledTabButton
          active={activeTab === "client_notifications"}
          onClick={() => setActiveTab("client_notifications")}
        >
          <FontAwesomeIcon icon={faUsers} /> Clientes
        </StyledTabButton>
      </TabsContainer>

      {activeTab === "restaurant_notifications" && (
        <RestaurantNotifications restaurant_id={restaurant_id} />
      )}

      {activeTab === "client_notifications" && (
        <Form onSubmit={handleSubmit}>
          <GridContainer style={{ marginTop: "10px" }}>
            {/*             <SettingsCard>
              <SectionTitle>Reconfirmações</SectionTitle>

              <Text>
                <FieldRow>
                  <span>
                    Enviar pedido de reconfirmação se a reserva for marcada com
                    mais de:
                  </span>

                  <SmallNumberInput
                    type="number"
                    min="0"
                    step="1"
                    inputMode="numeric"
                    value={reconfirmation_duration}
                    onChange={(e) => handleReconfirmation(e.target.value)} // "5"
                    placeholder="0"
                  />

                  <span>dias de antecedência.</span>
                </FieldRow>
              </Text>

              <Text>
                <FieldRow>
                  <span>Enviar pedido de reconfirmação ao cliente:</span>

                  <SmallNumberInput
                    type="number"
                    min="0"
                    step="1"
                    inputMode="numeric"
                    value={reconfirmation_time_before_date}
                    onChange={
                      (e) =>
                        handleReconfirmation_time_before_date(e.target.value) // "5"
                    }
                    placeholder="0"
                  />

                  <span> dias antes da reserva.</span>
                </FieldRow>

                {(didTrySave || touchedBeforeDate) &&
                  reconfirmationCompareError && (
                    <InlineError>{reconfirmationCompareError}</InlineError>
                  )}
              </Text>
            </SettingsCard> */}

            {/* ✅ REST OF YOUR CARDS STAY AS BEFORE (still use InputDayOrHour) */}
            <SettingsCard>
              <SectionTitle>Lembretes</SectionTitle>

              <Text>
                <FieldRow>
                  <span>Enviar lembrete da reserva ao cliente</span>
                  <InputDayOrHour
                    initialValue={
                      restaurants?.send_message_reminder_to_customers
                    }
                    setValue={handleMessageReminderTime}
                  />
                  <span>antes da hora da reserva.</span>
                </FieldRow>
              </Text>
            </SettingsCard>

            <SettingsCard>
              <SectionTitle>Notificações</SectionTitle>

              <Text>
                <FieldRow>
                  <span>
                    Receber notificação se o cliente cancelar com menos de
                  </span>
                  <InputDayOrHour
                    initialValue={restaurants?.time_to_cancelled_warning}
                    setValue={handleTimeCancelWarning}
                  />
                  <span>de antecedência.</span>
                </FieldRow>
              </Text>
            </SettingsCard>
          </GridContainer>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "20px",
            }}
          >
            <ModernButton type="submit" color="#EE754C" fontSize="14px">
              Guardar
            </ModernButton>
          </div>
        </Form>
      )}
    </Container>
  );
}

export default Notifications;

const Form = styled.form`
  width: 100%;
`;

const StyledTabButton = styled.button`
  font-size: 15px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 7px;
  background-color: ${(props) => (props.active ? "#ee754c" : "#f2f2f2")};
  color: ${(props) => (props.active ? "white" : "#333")};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: ${(props) => (props.active ? "#d96945" : "#e6e6e6")};
  }

  @media (max-width: 768px) {
    font-size: 14px;
    flex: 1 1 auto;
    justify-content: center;
  }
`;

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
  font-size: 14px;
  color: #6c757d;
`;

const SmallNumberInput = styled.input`
  width: 90px;
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  background: #fafafa;
  outline: none;

  &:focus {
    border-color: #ee754c;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(238, 117, 76, 0.18);
  }
`;

const InlineError = styled.div`
  margin-top: 6px;
  color: #dc3545;
  font-size: 12px;
`;
